"use client";

import Script from "next/script";
import { usePathname } from "next/navigation";
import dynamic from "next/dynamic";
import { createContext, Suspense, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { attributionFromUrl } from "@/lib/enquiries/attribution";
import type { MarketingAttribution } from "@/lib/enquiries/integrations";

const FloatingContactActions = dynamic(
  () => import("@/components/shared/FloatingContactActions").then((mod) => mod.FloatingContactActions),
  { ssr: false }
);

const ChatWidget = dynamic(
  () => import("@/components/shared/ChatWidget").then((mod) => mod.ChatWidget),
  { ssr: false }
);

type PublicIntegrationContextValue = {
  prepareEnquiryContext: (formName: string) => Promise<{
    formName: string;
    pageUrl: string;
    referrer: string;
    attribution: MarketingAttribution;
    recaptchaToken?: string;
  }>;
  trackLeadSubmission: (formName: string, enquiryId?: string) => void;
};

type Props = {
  children: React.ReactNode;
  siteEnvironment: "local" | "testing" | "production";
  recaptchaSiteKey?: string;
  gtmId?: string;
  tawkPropertyId?: string;
  tawkWidgetId?: string;
};

type TawkApi = {
  hideWidget?: () => void;
  showWidget?: () => void;
  onLoad?: () => void;
};

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
    grecaptcha?: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
    Tawk_API?: TawkApi;
    Tawk_LoadStart?: Date;
    Tawk_API_Loaded?: boolean;
  }
}

const ATTRIBUTION_STORAGE_KEY = "dermalife.initial-attribution.v1";

const PublicIntegrationContext = createContext<PublicIntegrationContextValue | null>(null);

function isProductionBrowserHost() {
  if (typeof window === "undefined") return false;
  const hostname = window.location.hostname.toLowerCase();
  return hostname === "dermalife.co.in" || hostname === "www.dermalife.co.in";
}

function readOrCaptureAttribution() {
  try {
    const existing = window.sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    if (existing) return JSON.parse(existing) as MarketingAttribution;
    const attribution = attributionFromUrl(window.location.href, document.referrer);
    window.sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
    return attribution;
  } catch {
    return attributionFromUrl(window.location.href, document.referrer);
  }
}

async function waitForRecaptcha() {
  const startedAt = Date.now();
  while (!window.grecaptcha) {
    if (Date.now() - startedAt > 8_000) throw new Error("Verification could not load. Please try again.");
    await new Promise((resolve) => window.setTimeout(resolve, 100));
  }
  await new Promise<void>((resolve) => window.grecaptcha?.ready(resolve));
  return window.grecaptcha;
}

function useDeferredChatLoad(isExcludedRoute: boolean, delayMs = 3500) {
  const [shouldLoadChat, setShouldLoadChat] = useState(false);

  useEffect(() => {
    if (isExcludedRoute || shouldLoadChat) return;

    let timer: ReturnType<typeof setTimeout> | undefined;
    let idleId: number | undefined;

    const scheduleChatLoad = () => {
      timer = setTimeout(() => {
        if (typeof window !== "undefined" && "requestIdleCallback" in window) {
          idleId = window.requestIdleCallback(() => setShouldLoadChat(true), { timeout: 1500 });
        } else {
          setShouldLoadChat(true);
        }
      }, delayMs);
    };

    if (typeof document !== "undefined" && document.readyState === "complete") {
      scheduleChatLoad();
    } else if (typeof window !== "undefined") {
      window.addEventListener("load", scheduleChatLoad, { once: true });
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("load", scheduleChatLoad);
      }
      if (timer) clearTimeout(timer);
      if (idleId && typeof window !== "undefined" && "cancelIdleCallback" in window) {
        window.cancelIdleCallback(idleId);
      }
    };
  }, [isExcludedRoute, shouldLoadChat, delayMs]);

  return shouldLoadChat;
}

function RuntimeIntegrationScripts({
  recaptchaSiteKey,
  gtmEnabled,
  gtmId,
  tawkPropertyId,
  tawkWidgetId,
  pushEvent,
  shouldLoadChat,
}: {
  recaptchaSiteKey?: string;
  gtmEnabled: boolean;
  gtmId?: string;
  tawkPropertyId?: string;
  tawkWidgetId?: string;
  pushEvent: (event: string, values?: Record<string, unknown>) => void;
  shouldLoadChat: boolean;
}) {
  const pathname = usePathname();
  const isPrivateRoute = pathname.startsWith("/admin");
  const isLandingPage = pathname.startsWith("/ht-lp") || pathname.startsWith("/landing-page");
  const isExcludedRoute = isPrivateRoute || isLandingPage;

  useEffect(() => {
    if (!gtmEnabled || isExcludedRoute) return;
    const onClick = (event: MouseEvent) => {
      const link = event.target instanceof Element ? event.target.closest<HTMLAnchorElement>("a[href]") : null;
      if (!link) return;
      const href = link.href;
      if (href.startsWith("tel:")) pushEvent("phone_click", { page_path: window.location.pathname });
      if (/wa\.me|whatsapp\.com/i.test(href)) pushEvent("whatsapp_click", { page_path: window.location.pathname });
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, [gtmEnabled, isExcludedRoute, pushEvent]);

  useEffect(() => {
    if (!isExcludedRoute && /^\/(?:thank-?you|blog\/thank-you)\/?$/i.test(pathname)) {
      pushEvent("thank_you_view", { page_path: pathname });
    }
  }, [isExcludedRoute, pathname, pushEvent]);

  useEffect(() => {
    if (!shouldLoadChat || isExcludedRoute) return;

    window.Tawk_API = window.Tawk_API || {};

    const updateWidgetVisibility = () => {
      const isMobile = window.matchMedia("(max-width: 767px)").matches;
      if (isExcludedRoute || isMobile) {
        window.Tawk_API?.hideWidget?.();
      } else {
        window.Tawk_API?.showWidget?.();
      }
    };

    window.Tawk_API.onLoad = updateWidgetVisibility;
    updateWidgetVisibility();

    window.addEventListener("resize", updateWidgetVisibility);
    return () => window.removeEventListener("resize", updateWidgetVisibility);
  }, [isExcludedRoute, shouldLoadChat]);

  const tawkEnabled = !isExcludedRoute && Boolean(tawkPropertyId && tawkWidgetId) && shouldLoadChat;
  return (
    <>
      {recaptchaSiteKey && !isPrivateRoute && (
        <Script
          id="dermalife-recaptcha"
          src={`https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(recaptchaSiteKey)}`}
          strategy="lazyOnload"
        />
      )}
      {gtmEnabled && gtmId && !isPrivateRoute && (
        <Script id="dermalife-gtm" strategy="lazyOnload">
          {`if(window.location.hostname==='dermalife.co.in'||window.location.hostname==='www.dermalife.co.in'){(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${gtmId}');}`}
        </Script>
      )}
      {tawkEnabled && tawkPropertyId && tawkWidgetId && (
        <Script id="dermalife-tawk" strategy="lazyOnload">
          {`if(!window.Tawk_API_Loaded){window.Tawk_API_Loaded=true;window.Tawk_API=window.Tawk_API||{};window.Tawk_LoadStart=new Date();(function(){var s1=document.createElement('script'),s0=document.getElementsByTagName('script')[0];s1.async=true;s1.src='https://embed.tawk.to/${tawkPropertyId}/${tawkWidgetId}';s1.charset='UTF-8';s1.setAttribute('crossorigin','*');s0.parentNode.insertBefore(s1,s0);})();}`}
        </Script>
      )}
    </>
  );
}

export function PublicIntegrationsProvider({
  children,
  siteEnvironment,
  recaptchaSiteKey,
  gtmId,
  tawkPropertyId,
  tawkWidgetId,
}: Props) {
  const pathname = usePathname();
  const isPrivateRoute = pathname.startsWith("/admin");
  const isLandingPage = pathname.startsWith("/ht-lp") || pathname.startsWith("/landing-page");
  const isExcludedRoute = isPrivateRoute || isLandingPage;

  const shouldLoadChat = useDeferredChatLoad(isExcludedRoute, 3500);

  useEffect(() => {
    readOrCaptureAttribution();
  }, []);

  const gtmEnabled = siteEnvironment === "production" && Boolean(gtmId);

  const pushEvent = useCallback(
    (event: string, values: Record<string, unknown> = {}) => {
      if (!gtmEnabled || !isProductionBrowserHost()) return;
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event, ...values });
    },
    [gtmEnabled],
  );

  const prepareEnquiryContext = useCallback(
    async (formName: string) => {
      let recaptchaToken: string | undefined;
      if (recaptchaSiteKey) {
        const recaptcha = await waitForRecaptcha();
        recaptchaToken = await recaptcha?.execute(recaptchaSiteKey, { action: "validate_captcha" });
      }
      return {
        formName,
        pageUrl: window.location.href,
        referrer: document.referrer,
        attribution: readOrCaptureAttribution(),
        recaptchaToken,
      };
    },
    [recaptchaSiteKey],
  );

  const trackLeadSubmission = useCallback(
    (formName: string, enquiryId?: string) => {
      pushEvent("lead_submission", {
        form_name: formName,
        enquiry_id: enquiryId,
        page_path: window.location.pathname,
      });
    },
    [pushEvent],
  );

  const contextValue = useMemo(
    () => ({ prepareEnquiryContext, trackLeadSubmission }),
    [prepareEnquiryContext, trackLeadSubmission],
  );

  return (
    <PublicIntegrationContext.Provider value={contextValue}>
      {children}
      <Suspense fallback={null}>
        <FloatingContactActions />
        {shouldLoadChat && !isExcludedRoute && <ChatWidget />}
        <RuntimeIntegrationScripts
          recaptchaSiteKey={recaptchaSiteKey}
          gtmEnabled={gtmEnabled}
          gtmId={gtmId}
          tawkPropertyId={tawkPropertyId}
          tawkWidgetId={tawkWidgetId}
          pushEvent={pushEvent}
          shouldLoadChat={shouldLoadChat}
        />
      </Suspense>
    </PublicIntegrationContext.Provider>
  );
}

export function usePublicIntegrations() {
  const context = useContext(PublicIntegrationContext);
  if (!context) throw new Error("usePublicIntegrations must be used inside PublicIntegrationsProvider");
  return context;
}
